// src/components/admin/page-management/AiPageGenerator.js
import {
    AlertCircle,
    CheckCircle,
    Globe,
    Loader2,
    Plus,
    X
} from 'lucide-react';
import React, { useState } from 'react';
import axios from '../../../utils/axiosHandler';

const AiPageGenerator = ({ onPageCreated, onClose }) => {
    const [url, setUrl] = useState('');
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const analyzeWebsite = async () => {
        if (!url.trim()) {
            setError('Please enter a valid URL');
            return;
        }

        // Basic URL validation
        try {
            new URL(url);
        } catch (error) {
            setError('Please enter a valid URL (e.g., https://example.com)');
            return;
        }

        setIsAnalyzing(true);
        setError(null);
        setAnalysisResult(null);

        try {
            const response = await axios.post('/api/page-ai/analyze-website', {
                url: url.trim()
            }, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            });

            setAnalysisResult(response.data);
            setSuccess('Website analyzed successfully!');
        } catch (error) {
            console.error('Error analyzing website:', error);
            setError(error.response?.data?.error || 'Failed to analyze website. Please try again.');
        } finally {
            setIsAnalyzing(false);
        }
    };

    const createPage = async () => {
        if (!analysisResult?.pageStructure) {
            setError('No page structure available. Please analyze a website first.');
            return;
        }

        setIsCreating(true);
        setError(null);

        try {
            const response = await axios.post('/api/page-ai/create-page', {
                pageStructure: analysisResult.pageStructure
            }, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            });

            setSuccess('Page created successfully!');

            // Call the callback to notify parent component
            if (onPageCreated) {
                onPageCreated(response.data.page);
            }
        } catch (error) {
            console.error('Error creating page:', error);
            setError(error.response?.data?.error || 'Failed to create page. Please try again.');
        } finally {
            setIsCreating(false);
        }
    };

    const clearResults = () => {
        setAnalysisResult(null);
        setError(null);
        setSuccess(null);
        setUrl('');
    };

    const renderWebsiteContent = () => {
        if (!analysisResult?.websiteContent) return null;

        const content = analysisResult.websiteContent;

        return (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h3 className="text-lg font-semibold mb-3">Website Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <strong>Title:</strong> {content.title || 'N/A'}
                    </div>
                    <div>
                        <strong>Description:</strong> {content.metaDescription || 'N/A'}
                    </div>
                    <div>
                        <strong>Main Heading:</strong> {content.h1Text || 'N/A'}
                    </div>
                    <div>
                        <strong>Images Found:</strong> {content.images?.length || 0}
                    </div>
                    <div>
                        <strong>Forms Found:</strong> {content.forms?.length || 0}
                    </div>
                    <div>
                        <strong>Navigation Items:</strong> {content.navigation?.length || 0}
                    </div>
                </div>
            </div>
        );
    };

    const renderPageStructure = () => {
        if (!analysisResult?.pageStructure) return null;

        const structure = analysisResult.pageStructure;

        return (
            <div className="bg-white border rounded-lg p-4 mb-4">
                <h3 className="text-lg font-semibold mb-3">Generated Page Structure</h3>
                <div className="space-y-3">
                    <div>
                        <strong>Page Title:</strong> {structure.pageTitle}
                    </div>
                    <div>
                        <strong>Page Slug:</strong> {structure.pageSlug}
                    </div>
                    <div>
                        <strong>Description:</strong> {structure.pageDescription}
                    </div>
                    <div>
                        <strong>Sections ({structure.sections?.length || 0}):</strong>
                        <div className="mt-2 space-y-2">
                            {structure.sections?.map((section, index) => (
                                <div key={index} className="flex items-center space-x-2 text-sm">
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                    <span className="font-medium">{section.type}</span>
                                    <span className="text-gray-500">(Order: {section.order})</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                        <Globe className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">AI Page Generator</h2>
                        <p className="text-gray-600">Generate pages from any website using AI</p>
                    </div>
                </div>
                {onClose && (
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                )}
            </div>

            {/* URL Input */}
            <div className="mb-6">
                <label htmlFor="website-url" className="block text-sm font-medium text-gray-700 mb-2">
                    Website URL
                </label>
                <div className="flex space-x-3">
                    <input
                        type="url"
                        id="website-url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://example.com"
                        className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={isAnalyzing}
                    />
                    <button
                        onClick={analyzeWebsite}
                        disabled={isAnalyzing || !url.trim()}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
                    >
                        {isAnalyzing ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Analyzing...</span>
                            </>
                        ) : (
                            <>
                                <Globe className="w-4 h-4" />
                                <span>Analyze Website</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Error Messages */}
            {error && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    <p>{error}</p>
                </div>
            )}

            {/* Success Messages */}
            {success && (
                <div className="mb-4 bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-md flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    <p>{success}</p>
                </div>
            )}

            {/* Analysis Results */}
            {analysisResult && (
                <div className="space-y-4">
                    {renderWebsiteContent()}
                    {renderPageStructure()}

                    {/* Action Buttons */}
                    <div className="flex space-x-3">
                        <button
                            onClick={createPage}
                            disabled={isCreating}
                            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
                        >
                            {isCreating ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span>Creating Page...</span>
                                </>
                            ) : (
                                <>
                                    <Plus className="w-4 h-4" />
                                    <span>Create Page</span>
                                </>
                            )}
                        </button>
                        <button
                            onClick={clearResults}
                            className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            Start Over
                        </button>
                    </div>
                </div>
            )}

            {/* Instructions */}
            <div className="mt-8 bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">How it works:</h3>
                <ol className="text-sm text-blue-800 space-y-1">
                    <li>1. Enter any website URL you want to replicate</li>
                    <li>2. AI will analyze the website content and structure</li>
                    <li>3. AI generates a page using your existing section types</li>
                    <li>4. For content that doesn't match existing sections, AI uses Rich Text with HTML/CSS</li>
                    <li>5. Review the generated structure and create the page</li>
                </ol>
            </div>
        </div>
    );
};

export default AiPageGenerator; 